import { PlacesAutocompleteResponseV2, PlacesDetailResponseV2 } from '@/models/dto'
import { deepClone } from './deepClone'
import {
  WheelsSearchResult,
  WheelsPickableAddress,
} from '@/components/WheelsAddressPicker/WheelsAddressPicker.constants'

/**
 * Constructs an array of address type objects from given keys.
 *
 * @param types - An array of keys (string) for generating address types.
 * @returns An array of address type objects, deeply cloned.
 */
export const buildAddressTypes = (types: string[] | null | undefined): any[] => {
  const addressTypes = (types || []).map((key) => ({
    addressTypeId: null,
    label: null,
    key,
    description: null,
  }))
  return deepClone(addressTypes)
}
/**
 * Converts a PlacesAutocompleteResponseV2 object to a WheelsSearchResult object.
 *
 * @param placesResponse - The PlacesAutocompleteResponseV2 object to convert.
 * @returns A WheelsSearchResult object containing the id, source, description, and types from the placesResponse.
 */
export function convertPlacesAutocompleteResponseV2ToWheelsSearchResponse(
  placesResponse: PlacesAutocompleteResponseV2,
): WheelsSearchResult {
  return {
    id: placesResponse.id,
    source: placesResponse.source,
    description: placesResponse.description,
    types: placesResponse.types,
  }
}

/**
 * Converts a PlacesDetailResponseV2 object to a WheelsPickableAddress object.
 * Maps the fields from the Google Places API response format to the internal address format.
 *
 * @param detail - The PlacesDetailResponseV2 object containing place details from the API
 * @return WheelsPickableAddress object with mapped fields for internal use
 */
export function convertPlacesDetailResponseV2ToWheelsAddress(detail: PlacesDetailResponseV2): WheelsPickableAddress {
  return {
    title: detail.title,
    name: detail.addressName,
    street1: detail.street1,
    city: detail.city,
    state: detail.state,
    placeId: null, // Not available in PlacesDetailResponseV2
    postalCode: detail.postalCode,
    country: detail.country,
    lat: detail.lat.toString(),
    lng: detail.lng.toString(),
    zoneId: detail.timeZone,
  }
}

/**
 * Checks whether the 'airport' address type is present in the given types.
 *
 * @param types - An array of strings or objects (with a 'key' property) representing address types.
 * @returns `true` if 'airport' is found among the types, `false` otherwise.
 */
export const isAirportAddress = (types: (string | { key?: string })[] | null | undefined): boolean => {
  if (!types || types.length === 0) {
    return false
  }

  return types.some((type) => {
    // Using type guard to differentiate between string and object with 'key'
    if (typeof type === 'string') {
      return type === 'airport'
    } else {
      return type.key === 'airport'
    }
  })
}

export const buildAddressName = (address): string => {
  let addressName = ''
  if (address?.street1 && address?.street1 !== ' ') {
    addressName = `${address.street1}, `
  }
  if (address?.city) {
    addressName = `${addressName}${address.city}, `
  }
  if (address?.state) {
    addressName = `${addressName}${address.state}`
  }
  return addressName
}

/**
 *
 * Formats an address into a single-line string that includes the street1, city, and state.
 * @param street - The string that contains the street information.
 * @param city - The string that contains the city information.
 * @param state - The string that contains the state information.
 * @returns A string with the street1, city, and state of the address.
 */
export const formatAddressToSingleLineSimple = (
  street?: string,
  city?: string,
  state?: string,
) => {
  const parts = [street?.trim(), city?.trim(), state?.trim()].filter(Boolean) // Remove any falsy values (e.g., null, undefined, empty strings)
  return parts.join(', ')
}
